import type { ReactNode } from "react";
import { useState } from "react";
import {
  fetchDecisions,
  type DecisionsResponse,
  type ScheduleSlotResult,
} from "../api";
import { useSlotsQuery } from "../hooks/useSlotsQuery.js";

interface PickedSlot {
  workOrderStatus: string;
  scheduleDate: string;   // YYYY-MM-DD
  startTime: string;      // HH:MM
  endTime: string;        // HH:MM
  technicianId: string;
}

interface Props {
  postcode: string;
  durationMinutes: number;
  estimatedValueGbp: number;
  jobRef?: string;
  onPick: (s: PickedSlot) => void;
  onClose: () => void;
}

const REJECT_LABEL: Record<string, string> = {
  NO_TRAVEL_DATA:        "No HERE travel cache for this postcode pair",
  DISTANCE_EXCEEDS_PREF: "Beyond tech's travel preference",
  BELOW_MIN_VALUE:       "Below tech's minimum job value",
  OVERLAP_VISIT:         "Conflicts with another Breeze booking",
  OVERLAP_FREEBUSY:      "Conflicts with the tech's free/busy calendar",
};

function fmtDateTime(iso: string): string {
  // Stored without TZ offset (server returns "2026-04-28T08:00:00") — display verbatim.
  const [d, t] = iso.split("T");
  if (!d || !t) return iso;
  const [y, m, day] = d.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(day));
  const dow = dt.toLocaleDateString("en-GB", { weekday: "short" });
  const dm = dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return `${dow} ${dm} · ${t.slice(0, 5)}`;
}

function isoDate(iso: string): string {
  return iso.split("T")[0] ?? iso;
}

function isoHM(iso: string): string {
  return (iso.split("T")[1] ?? "").slice(0, 5);
}

function dayShort(iso: string): string {
  const [y, m, d] = iso.split("T")[0]?.split("-") ?? [];
  if (!y || !m || !d) return "";
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString("en-GB", { weekday: "short" });
}

function dateShort(iso: string): string {
  const [y, m, d] = iso.split("T")[0]?.split("-") ?? [];
  if (!y || !m || !d) return iso;
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function fmtHrs(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, "0")}`;
}

type SortKey =
  | "score"
  | "tech"
  | "date"
  | "travel"
  | "total_time"
  | "hourly";

/**
 * Dispatch-Board-3-style flat table — every slot in one scrollable view, click
 * any column header to sort. Default sort is score descending so the agent's
 * top recommendations float to the top. Click a row to pick the slot.
 */
function SlotsTable({
  slots,
  billableValueGbp,
  durationMinutes,
  onPick,
}: {
  slots: ScheduleSlotResult[];
  billableValueGbp: number;
  durationMinutes: number;
  onPick: (s: ScheduleSlotResult) => void;
}): JSX.Element {
  const [sortKey, setSortKey] = useState<SortKey>("score");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function setSort(k: SortKey): void {
    if (k === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      // Sensible default direction per column: time/date asc, everything else desc
      setSortDir(k === "date" || k === "travel" || k === "total_time" ? "asc" : "desc");
    }
  }

  // Per-row derived values (total time, tech earnings, hourly).
  const rows = slots.map((s) => {
    const travelMin = s.travel_minutes ?? 0;
    const totalMin = durationMinutes + travelMin;
    const commission = s.tech_commission_pct ?? 0;
    const techEarnings = (billableValueGbp * commission) / 100;
    // £/hr denominator floored at 60 min — realistic minimum overhead
    // per job (arrive · unload · clean · pack · payment). Stops
    // zero-travel slots from showing inflated rates that assume the tech
    // could chain identical jobs back-to-back.
    const hourlyMin = Math.max(60, totalMin);
    const hourly = techEarnings / (hourlyMin / 60);
    return { s, travelMin, totalMin, commission, techEarnings, hourly };
  });

  const sorted = [...rows].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    switch (sortKey) {
      case "score":
        return dir * (a.s.score - b.s.score);
      case "tech":
        return dir * a.s.tech_name.localeCompare(b.s.tech_name);
      case "date":
        return dir * a.s.slot_start.localeCompare(b.s.slot_start);
      case "travel":
        return dir * (a.travelMin - b.travelMin);
      case "total_time":
        return dir * (a.totalMin - b.totalMin);
      case "hourly":
        return dir * (a.hourly - b.hourly);
    }
  });

  function Header({ k, children }: { k: SortKey; children: ReactNode }): JSX.Element {
    const active = sortKey === k;
    const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "";
    return (
      <th
        onClick={() => setSort(k)}
        className={`cursor-pointer select-none px-2 py-1.5 text-left font-medium ${
          active ? "text-ink-50" : "text-ink-200 hover:text-ink-100"
        }`}
      >
        {children}
        {arrow ? <span className="ml-1 text-ink-400">{arrow}</span> : null}
      </th>
    );
  }

  return (
    <div>
      <div className="mb-2 text-[12px] text-ink-200">
        {slots.length} slots · click a column to sort · click a row to pick
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="border-b border-ink-700 bg-ink-800/70 text-ink-300">
            <tr>
              <Header k="score">Score</Header>
              <Header k="tech">Cleaner</Header>
              <Header k="date">Day · Date</Header>
              <th className="px-2 py-1.5 text-left font-medium text-ink-200">Time</th>
              <Header k="travel">Travel</Header>
              <th className="px-2 py-1.5 text-left font-medium text-ink-200">Distance</th>
              <Header k="total_time">Total</Header>
              <th className="px-2 py-1.5 text-right font-medium text-ink-200">
                Total £
              </th>
              <Header k="hourly">£/hr (tech)</Header>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700/60">
            {sorted.map(({ s, travelMin, totalMin, commission, hourly }) => {
              const lowEarnings = commission > 0 && hourly < 15;
              return (
                <tr
                  key={`${s.tech_id}-${s.slot_start}`}
                  onClick={() => onPick(s)}
                  className="cursor-pointer hover:bg-ink-800/80"
                >
                  <td className="px-2 py-1 text-ink-50 tabular-nums">{s.score}</td>
                  <td className="px-2 py-1 text-ink-50">
                    {s.tech_name}
                    {commission > 0 ? (
                      <span className="ml-1 text-[11px] text-ink-400">
                        ({commission.toFixed(0)}%)
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2 py-1">
                    <span className="text-ink-100">{dayShort(s.slot_start)}</span>{" "}
                    <span className="text-ink-200">{dateShort(s.slot_start)}</span>
                    {s.is_saturday ? (
                      <span className="ml-1 rounded bg-amber-700/40 px-1 py-0.5 text-[10px] text-amber-200">
                        SAT
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2 py-1 text-ink-200 tabular-nums">
                    {isoHM(s.slot_start)}–{isoHM(s.slot_end)}
                  </td>
                  <td className="px-2 py-1 text-ink-200 tabular-nums">
                    {travelMin}m
                    <span className="ml-1 text-[11px] text-ink-400">
                      {s.travel_origin === "home"
                        ? `from home (${s.travel_from_postcode ?? "?"})`
                        : s.travel_origin === "previous_unknown"
                          ? "after prior block (default)"
                          : `from ${s.travel_from_postcode ?? "?"}`}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-ink-300 tabular-nums">
                    {s.distance_miles != null ? `${s.distance_miles.toFixed(1)}mi` : "—"}
                  </td>
                  <td className="px-2 py-1 text-ink-200 tabular-nums">
                    {fmtHrs(totalMin)}
                  </td>
                  <td className="px-2 py-1 text-right text-ink-200 tabular-nums">
                    £{billableValueGbp.toFixed(2)}
                  </td>
                  <td
                    className={`px-2 py-1 tabular-nums ${
                      commission === 0
                        ? "text-ink-400"
                        : lowEarnings
                          ? "text-red-400"
                          : "text-emerald-300"
                    }`}
                  >
                    {commission > 0 ? `£${hourly.toFixed(2)}/hr` : "no commission set"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FindSlotsModal({
  postcode,
  durationMinutes,
  estimatedValueGbp,
  jobRef,
  onPick,
  onClose,
}: Props): JSX.Element {
  // Shared cache with QuoteEditor's background prefetch. If the parent
  // already fired the query, this resolves instantly from cache.
  const slots = useSlotsQuery({
    postcode,
    durationMinutes,
    estimatedValueGbp,
    jobRef,
  });
  const loading = slots.isPending || slots.isFetching;
  const error = slots.error
    ? slots.error instanceof Error
      ? slots.error.message
      : String(slots.error)
    : null;
  const response = slots.data ?? null;

  const [decisions, setDecisions] = useState<DecisionsResponse | null>(null);
  const [showLog, setShowLog] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);

  async function loadLog(): Promise<void> {
    if (!response) return;
    if (decisions) {
      setShowLog(true);
      return;
    }
    setLogLoading(true);
    setLogError(null);
    try {
      const d = await fetchDecisions(response.request_id);
      setDecisions(d);
      setShowLog(true);
    } catch (e) {
      setLogError(e instanceof Error ? e.message : String(e));
    } finally {
      setLogLoading(false);
    }
  }

  function handlePick(slot: ScheduleSlotResult): void {
    onPick({
      workOrderStatus: "Booking made (Converted)",
      scheduleDate: isoDate(slot.slot_start),
      startTime: isoHM(slot.slot_start),
      endTime: isoHM(slot.slot_end),
      technicianId: String(slot.tech_id),
    });
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-[min(960px,96vw)] flex-col overflow-hidden rounded-lg border border-ink-500 bg-ink-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-ink-700 px-4 py-3">
          <div>
            <div className="text-[14px] font-medium text-ink-50">Find available slots</div>
            <div className="text-[12px] text-ink-200">
              {postcode || "(no postcode)"} · {durationMinutes} min · £
              {estimatedValueGbp.toFixed(0)}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ink-500 px-2 py-1 text-[13px] text-ink-50 hover:border-ink-400"
          >
            Close
          </button>
        </div>

        <div className="grow overflow-y-auto px-4 py-4">
          {loading ? (
            <div className="py-8 text-center text-[13px] text-ink-200">
              Searching slots over the next 14 days…
            </div>
          ) : error ? (
            <div className="rounded-md border border-red-700 bg-red-950/30 px-3 py-2 text-[13px] text-red-300">
              {error}
            </div>
          ) : !response ? null : response.slots.length === 0 ? (
            <div className="space-y-3">
              <div className="rounded-md border border-amber-700 bg-amber-950/20 px-3 py-2 text-[13px] text-amber-200">
                No slots matched. {response.rejected_count} candidates evaluated
                and rejected. Click "Why these slots?" below to inspect the
                decision trail — it will show whether techs were ruled out by
                distance, calendar conflicts, missing travel data, etc.
              </div>
            </div>
          ) : (
            <SlotsTable
              slots={response.slots}
              billableValueGbp={estimatedValueGbp}
              durationMinutes={durationMinutes}
              onPick={handlePick}
            />
          )}

          {/* Why these slots? expander — always available once /find returned */}
          {response ? (
            <div className="mt-5 border-t border-ink-700 pt-3">
              <button
                type="button"
                onClick={loadLog}
                className="text-[12px] text-ink-200 underline-offset-2 hover:text-ink-50 hover:underline"
                disabled={logLoading}
              >
                {showLog
                  ? "Hide decision log"
                  : logLoading
                    ? "Loading log…"
                    : "Why these slots? (decision log)"}
              </button>
              {showLog && decisions ? (
                <div className="mt-3 space-y-3">
                  <div className="text-[12px] text-ink-200">
                    {decisions.summary.recommended} recommended ·{" "}
                    {decisions.summary.scored} scored ·{" "}
                    {decisions.summary.rejected} rejected ·{" "}
                    {decisions.summary.total} evaluated
                  </div>
                  {Object.keys(decisions.rejection_histogram).length > 0 ? (
                    <div className="rounded-md border border-ink-700 bg-ink-800 px-3 py-2">
                      <div className="mb-1 text-[12px] font-medium text-ink-100">
                        Rejection breakdown
                      </div>
                      <div className="space-y-0.5">
                        {Object.entries(decisions.rejection_histogram)
                          .sort((a, b) => b[1] - a[1])
                          .map(([code, n]) => (
                            <div
                              key={code}
                              className="flex justify-between text-[12px] text-ink-200"
                            >
                              <span title={REJECT_LABEL[code] ?? ""}>
                                {code}
                                {REJECT_LABEL[code] ? (
                                  <span className="ml-2 text-ink-300">
                                    {REJECT_LABEL[code]}
                                  </span>
                                ) : null}
                              </span>
                              <span className="tabular-nums">{n}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : null}

                  <details className="rounded-md border border-ink-700 bg-ink-800">
                    <summary className="cursor-pointer px-3 py-2 text-[12px] text-ink-100">
                      Per-candidate trail ({decisions.entries.length} rows)
                    </summary>
                    <div className="max-h-[40vh] overflow-y-auto px-3 pb-2">
                      <table className="w-full text-[11px] text-ink-200">
                        <thead className="sticky top-0 bg-ink-800 text-ink-300">
                          <tr>
                            <th className="py-1 text-left">Outcome</th>
                            <th className="text-left">Tech</th>
                            <th className="text-left">When</th>
                            <th className="text-right">Score</th>
                            <th className="text-left">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {decisions.entries.map((e) => (
                            <tr
                              key={e.id}
                              className={
                                e.outcome === "recommended"
                                  ? "bg-emerald-900/20"
                                  : e.outcome === "scored"
                                    ? ""
                                    : "text-ink-300"
                              }
                            >
                              <td className="py-0.5 pr-2">{e.outcome}</td>
                              <td className="pr-2">
                                {e.tech_name ?? `#${e.tech_id}`}
                              </td>
                              <td className="pr-2">
                                {e.candidate_date} {e.candidate_time?.slice(0, 5)}
                              </td>
                              <td className="pr-2 text-right tabular-nums">
                                {e.score ?? ""}
                              </td>
                              <td>
                                {e.rejection_code ?? ""}
                                {e.notes ? (
                                  <span className="ml-1 text-ink-400">
                                    — {e.notes}
                                  </span>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
